/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/// <reference types="office-js" />

/**
 * Low-level wrapper around Office.context.document.customXmlParts.
 *
 * Handles the async callback pattern of the Office.js API and presents a
 * Promise-based interface.
 *
 * This module is the ONLY place in the monorepo that touches
 * Office.context.document.customXmlParts directly.
 *
 * @see https://learn.microsoft.com/en-us/javascript/api/office/office.customxmlparts
 */

function getCustomXmlParts(): Office.CustomXmlParts {
  const parts = globalThis.Office?.context?.document?.customXmlParts;
  if (parts === undefined) {
    throw new Error('Office.context.document.customXmlParts is not available');
  }
  return parts;
}

function invokeAsync<T>(
  start: (callback: (result: Office.AsyncResult<T>) => void) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    start((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(result.error.message));
        return;
      }
      resolve(result.value);
    });
  });
}

/** Find custom XML parts by namespace URI; returns part IDs. */
export async function findPartsByNamespace(namespaceUri: string): Promise<string[]> {
  const collection = await invokeAsync<Office.CustomXmlPart[]>((cb) => {
    getCustomXmlParts().getByNamespaceAsync(namespaceUri, cb);
  });
  return collection.map((p) => p.id);
}

/** Get the XML content of a custom XML part by ID */
export async function getPartXml(partId: string): Promise<string> {
  const part = await invokeAsync<Office.CustomXmlPart | null>((cb) => {
    getCustomXmlParts().getByIdAsync(partId, cb);
  });
  if (part === null) {
    throw new Error(`Custom XML part not found: ${partId}`);
  }
  return invokeAsync<string>((cb) => {
    part.getXmlAsync(cb);
  });
}

/** Add a new custom XML part with the given XML content; returns new part ID */
export async function addPart(xml: string): Promise<string> {
  const part = await invokeAsync<Office.CustomXmlPart>((cb) => {
    getCustomXmlParts().addAsync(xml, cb);
  });
  return part.id;
}

/** Delete a custom XML part by ID */
export async function deletePart(partId: string): Promise<void> {
  const part = await invokeAsync<Office.CustomXmlPart | null>((cb) => {
    getCustomXmlParts().getByIdAsync(partId, cb);
  });
  if (part === null) {
    throw new Error(`Custom XML part not found: ${partId}`);
  }
  await invokeAsync<void>((cb) => {
    part.deleteAsync(cb);
  });
}
